import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { useTranslation } from 'react-i18next';
import { appDateTime } from '@/utils/appDateFormat';
import { EmailTaskStatus } from '../../../models/deps';
import {
  getEmailQueueSummary,
  getEmailTasks,
  SendQueuedEmails,
  updateEmailTaskStatus,
  type EmailQueueSummary,
  type EmailTaskListItem,
  type EmailTaskPage,
} from '../../../api/General/Notification';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '../../../components/ui/Table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter
} from '../../../components/ui/Dialog';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { StatusBadge, type StatusBadgeProps } from '../../../components/ui/StatusBadge';
import { Alert } from '../../../components/ui/Alert';
import { Tooltip } from '../../../components/ui/Tooltip';
import { hasApiErrorCode } from '../../../utils/requestHandling';
import { Skeleton } from '../../../components/ui/Skeleton';
import Pagination from '../../../components/Pagination';
import { PageLayout } from '../../../components/ui/PageLayout';
import { cn } from '../../../utils/cn';
import { Mail } from 'lucide-react';

/*
 * Operator-only mail-queue triage (CO-846, CO-847, CO-848).
 *
 * The EmailQueuerTasks table has no CompanyId, so this screen spans every tenant. The wire
 * (EmailTaskListItem in api/General/Notification.ts) therefore carries operational fields only:
 * no recipient address, subject, template model, CC or attachment path. `toMasked` is first
 * character plus domain, `template` is the PII-free triage key, and `lastError` arrives already
 * scrubbed of addresses. Paging and the status filter run on the server; the summary above the
 * table is one extra read per load or refresh, never on a timer (every SQL command on production
 * is a paid telemetry row).
 */

const COLUMN_COUNT = 10;

/** CO-959: the server's refusal code for re-arming a scrubbed welcome mail. */
const WELCOME_REARM_BLOCKED = 'WelcomeRearmBlocked';
const PAGE_SIZE_OPTIONS = [10, 25, 50];

const STATUS_ORDER: EmailTaskStatus[] = [
  EmailTaskStatus.Pending,
  EmailTaskStatus.Sending,
  EmailTaskStatus.Error,
  EmailTaskStatus.PermanentlyFailed,
  EmailTaskStatus.Sent,
];

const STATUS_META: Record<EmailTaskStatus, { variant: StatusBadgeProps['variant']; labelKey: string }> = {
  [EmailTaskStatus.Pending]: { variant: 'info', labelKey: 'common:Pending' },
  [EmailTaskStatus.Sending]: { variant: 'warning', labelKey: 'common:Sending' },
  [EmailTaskStatus.Sent]: { variant: 'success', labelKey: 'common:Sent' },
  [EmailTaskStatus.Error]: { variant: 'error', labelKey: 'common:Error' },
  [EmailTaskStatus.PermanentlyFailed]: { variant: 'error', labelKey: 'common:Permanently Failed' },
};

const formatUtc = (value?: string | null): string => {
  const parsed = parseApiUtc(value);
  return parsed ? appDateTime(parsed, true) : '';
};

const SystemEmails: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState<EmailTaskStatus | null>(null);
  const [data, setData] = useState<EmailTaskPage | null>(null);
  const [summary, setSummary] = useState<EmailQueueSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [sending, setSending] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<EmailTaskListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<EmailTaskStatus>(EmailTaskStatus.Pending);
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const [rearmRefused, setRearmRefused] = useState(false);
  // A slow response for an earlier page or filter must not overwrite a newer one.
  const requestSeq = useRef(0);

  const loadTasks = useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await getEmailTasks({ page, pageSize, status: statusFilter ?? undefined });
      if (seq !== requestSeq.current) return;
      if (response.status === 200 && !response.errors?.length && response.payload) {
        setData(response.payload);
        setLoadFailed(false);
      } else {
        setLoadFailed(true);
      }
    } catch {
      if (seq === requestSeq.current) setLoadFailed(true);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [page, pageSize, statusFilter]);

  const loadSummary = useCallback(async () => {
    try {
      const response = await getEmailQueueSummary();
      if (response.status === 200 && !response.errors?.length && response.payload) {
        setSummary(response.payload);
      }
    } catch {
      // The summary is secondary: the table and its filter stay usable without the counts.
    }
  }, []);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const tasks = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // A status change can empty the page being viewed (the row left the filter): step back.
  useEffect(() => {
    if (!loading && page > totalPages) setPage(totalPages);
  }, [loading, page, totalPages]);

  const refresh = () => {
    loadTasks();
    loadSummary();
  };

  const changeFilter = (status: EmailTaskStatus | null) => {
    setStatusFilter(status);
    setPage(1);
    setExpandedId(null);
  };

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const sendQueuedEmails = async () => {
    setSending(true);
    try {
      const response = await SendQueuedEmails();
      if (response.status === 200) refresh();
    } catch {
      // The rows keep their state; the next refresh shows whether anything moved.
    } finally {
      setSending(false);
    }
  };

  const showUpdateStatusModal = (task: EmailTaskListItem) => {
    setSelectedTask(task);
    setNewStatus(task.status);
    setSaveFailed(false);
    setRearmRefused(false);
    setIsModalOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedTask) return;
    setSaving(true);
    setSaveFailed(false);
    setRearmRefused(false);
    try {
      const response = await updateEmailTaskStatus(selectedTask.id, newStatus);
      if (response.status === 200 && !response.errors?.length) {
        setIsModalOpen(false);
        refresh();
      } else {
        setSaveFailed(true);
        setRearmRefused(hasApiErrorCode(response, WELCOME_REARM_BLOCKED));
      }
    } catch (error) {
      setSaveFailed(true);
      setRearmRefused(hasApiErrorCode(error, WELCOME_REARM_BLOCKED));
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = (status: EmailTaskStatus) => {
    const meta = STATUS_META[status];
    return meta
      ? <StatusBadge variant={meta.variant}>{t(meta.labelKey)}</StatusBadge>
      : <StatusBadge variant="neutral">{t('common:Unknown')}</StatusBadge>;
  };

  const countFor = (status: EmailTaskStatus): number | null => {
    if (!summary) return null;
    return summary.statusCounts?.find((c) => c.status === status)?.count ?? 0;
  };

  const waitingAge = (): string => {
    const oldest = parseApiUtc(summary?.oldestWaitingCreatedOn);
    if (!oldest) return '';
    const totalMinutes = Math.max(0, Math.floor((Date.now() - oldest.getTime()) / 60000));
    const minutes = totalMinutes % 60;
    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours === 0) return t('common:{{minutes}} min', { minutes });
    if (totalHours < 48) return t('common:{{hours}} h {{minutes}} min', { hours: totalHours, minutes });
    return t('common:{{days}} d {{hours}} h', { days: Math.floor(totalHours / 24), hours: totalHours % 24 });
  };

  // CO-231: `PermanentlyFailed` belongs here, and it is not cosmetic. A row stuck in `Sending`
  // holds its place until the claim expires; forcing it to a TERMINAL status releases it at once
  // (this is how dev row 9925 was released). Moving a row back to `Pending`/`Error` re-arms it:
  // the server clears RetryCount/NextAttemptOn so the row is genuinely re-picked
  // (see NotificationService.UpdateEmailTaskStatusAsync).
  const statusOptions = [
    { value: EmailTaskStatus.Pending, label: t('common:Pending') },
    { value: EmailTaskStatus.Sending, label: t('common:Sending') },
    { value: EmailTaskStatus.Sent, label: t('common:Sent') },
    { value: EmailTaskStatus.Error, label: t('common:Error') },
    { value: EmailTaskStatus.PermanentlyFailed, label: t('common:Permanently Failed') },
  ];

  const filterChip = (status: EmailTaskStatus | null, label: string, count: number | null) => {
    const selected = statusFilter === status;
    return (
      <button
        key={status ?? 'all'}
        type="button"
        aria-pressed={selected}
        onClick={() => changeFilter(status)}
        className={cn(
          'inline-flex h-10 items-center gap-2 rounded-full border px-3 text-sm transition-colors sm:h-9',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
          selected
            ? 'border-primary bg-primary/10 font-medium text-primary'
            : 'border-border bg-card text-muted-foreground hover:bg-muted/50 hover:text-foreground'
        )}
      >
        <span>{label}</span>
        {count != null && <span className="tabular-nums">{count}</span>}
      </button>
    );
  };

  const waitingByCompany = summary?.waitingByCompany ?? [];
  const oldestWaitingAge = waitingAge();
  const hasRows = tasks.length > 0;

  return (
    <PageLayout
      title={t('common:System Emails')}
      icon={Mail}
      accentColor="slate"
      headerActions={(
        <div className="flex gap-2">
          <Button
            variant="outline"
            iconName="Send"
            onClick={sendQueuedEmails}
            loading={sending}
            disabled={sending}
          >
            {t('common:Send Queued Emails')}
          </Button>
          <Button
            variant="outline"
            iconName="RotateCcw"
            onClick={refresh}
            disabled={loading}
          >
            {t('common:Refresh')}
          </Button>
        </div>
      )}
    >
      <div className="rounded-lg border border-border bg-card">
        <div className="space-y-3 border-b border-border p-4">
          <div role="group" aria-label={t('common:Filter by status')} className="flex flex-wrap gap-2">
            {filterChip(null, t('common:All statuses'), null)}
            {STATUS_ORDER.map((status) => filterChip(status, t(STATUS_META[status].labelKey), countFor(status)))}
          </div>

          {summary && (
            <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
              <div className="flex items-baseline gap-1.5">
                <dt className="text-muted-foreground">{t('common:Waiting')}</dt>
                <dd className="font-medium tabular-nums text-foreground">{summary.waitingCount ?? 0}</dd>
              </div>
              {oldestWaitingAge && (
                <div className="flex flex-wrap items-baseline gap-1.5">
                  <dt className="text-muted-foreground">{t('common:Oldest waiting')}</dt>
                  <dd className="font-medium tabular-nums text-foreground">{oldestWaitingAge}</dd>
                  <dd className="tabular-nums text-muted-foreground">
                    {formatUtc(summary.oldestWaitingCreatedOn)}
                  </dd>
                </div>
              )}
            </dl>
          )}

          {waitingByCompany.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span id="mail-queue-waiting-by-company" className="text-muted-foreground">
                {t('common:Waiting by company')}
              </span>
              <ul aria-labelledby="mail-queue-waiting-by-company" className="flex flex-wrap gap-2">
                {waitingByCompany.map((lane) => (
                  <li
                    key={lane.companyName ?? ''}
                    className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-muted px-2 py-1 text-xs text-foreground"
                  >
                    <span className="truncate">{lane.companyName ?? t('common:No company')}</span>
                    <span className="font-medium tabular-nums">{lane.count}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {loadFailed && (
          <div className="border-b border-border p-4">
            <Alert variant="error">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="text-sm">{t('common:Failed to load email tasks')}</span>
                <Button variant="outline" size="sm" onClick={refresh} disabled={loading}>
                  {t('common:Retry')}
                </Button>
              </div>
            </Alert>
          </div>
        )}

        <Table aria-busy={loading} className={cn(loading && hasRows && 'opacity-60 transition-opacity')}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common:ID')}</TableHead>
              <TableHead>{t('common:To')}</TableHead>
              <TableHead>{t('common:Company')}</TableHead>
              <TableHead>{t('common:Priority')}</TableHead>
              <TableHead>{t('common:Status')}</TableHead>
              <TableHead>{t('common:Attempts')}</TableHead>
              <TableHead>{t('common:Next attempt')}</TableHead>
              <TableHead>{t('common:Created On')}</TableHead>
              <TableHead>{t('common:Sent On')}</TableHead>
              <TableHead>{t('common:Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && !hasRows ? (
              Array.from({ length: 5 }, (_, index) => (
                <TableRow key={`skeleton-${index}`} className="hover:bg-transparent">
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : !hasRows ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={COLUMN_COUNT} className="py-10 text-center text-muted-foreground">
                  {statusFilter
                    ? t('common:No email tasks with this status')
                    : t('common:No email tasks found')}
                </TableCell>
              </TableRow>
            ) : (
              tasks.map((task) => {
                const failed = task.status === EmailTaskStatus.Error
                  || task.status === EmailTaskStatus.PermanentlyFailed;
                const hasErrorDetail = failed || !!task.lastError;
                const expanded = expandedId === task.id;
                const detailId = `mail-queue-error-${task.id}`;
                return (
                  <Fragment key={task.id}>
                    <TableRow>
                      <TableCell className="tabular-nums">{task.id}</TableCell>
                      <TableCell>
                        <div className="max-w-[16rem] truncate" dir="ltr" title={task.toMasked}>
                          {task.toMasked}
                        </div>
                        {task.template && (
                          <div className="max-w-[16rem] truncate text-xs text-muted-foreground" dir="ltr">
                            {task.template}
                          </div>
                        )}
                        {task.rearmBlocked && (
                          <div className="max-w-[16rem] text-xs text-muted-foreground">
                            {t('common:Resend through HR with Send welcome email')}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="max-w-[12rem] truncate">{task.companyName ?? ''}</div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {(task.priority ?? 0) < 0
                          ? <span className="text-muted-foreground">{t('common:Bulk')}</span>
                          : t('common:Normal')}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{statusBadge(task.status)}</TableCell>
                      <TableCell className="tabular-nums">{task.retryCount ?? 0}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatUtc(task.nextAttemptOn)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {formatUtc(task.createdOn)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {task.sentOn
                          ? formatUtc(task.sentOn)
                          : <span className="text-muted-foreground">{t('common:Not sent yet')}</span>}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {task.rearmBlocked ? (
                            // CO-959: a scrubbed welcome cannot be re-sent (the server refuses it too).
                            // aria-disabled, not disabled, so keyboard focus still opens the tooltip.
                            <Tooltip content={t('common:This welcome email was already used and its password was removed, so it cannot be sent again. HR can send a new one with "Send welcome email" on the employee profile.')}>
                              <Button
                                variant="ghost"
                                size="icon"
                                iconName="Pencil"
                                aria-disabled="true"
                                aria-label={t('common:This welcome email was already used and its password was removed, so it cannot be sent again. HR can send a new one with "Send welcome email" on the employee profile.')}
                                className="cursor-not-allowed opacity-50 hover:bg-transparent"
                                data-testid={`mail-queue-rearm-blocked-${task.id}`}
                              />
                            </Tooltip>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              iconName="Pencil"
                              aria-label={t('common:Edit status of email {{id}}', { id: task.id })}
                              onClick={() => showUpdateStatusModal(task)}
                            />
                          )}
                          {hasErrorDetail && (
                            <Button
                              variant="ghost"
                              size="icon"
                              iconName={expanded ? 'ChevronUp' : 'ChevronDown'}
                              aria-label={t('common:Last error of email {{id}}', { id: task.id })}
                              aria-expanded={expanded}
                              aria-controls={detailId}
                              onClick={() => setExpandedId(expanded ? null : task.id)}
                            />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                    {expanded && (
                      <TableRow id={detailId} className="bg-muted/30 hover:bg-muted/30">
                        <TableCell colSpan={COLUMN_COUNT} className="py-3">
                          <div className="max-w-[110ch] space-y-1">
                            <div className="text-xs font-medium text-muted-foreground">
                              {t('common:Last error')}
                            </div>
                            {task.lastError ? (
                              <>
                                <p
                                  dir="ltr"
                                  className="whitespace-pre-wrap break-words font-mono text-xs text-foreground rtl:text-right"
                                >
                                  {task.lastError}
                                </p>
                                {task.lastErrorOn && (
                                  <p className="text-xs text-muted-foreground">
                                    {t('common:Last error time')}
                                    {': '}
                                    <span className="tabular-nums">{formatUtc(task.lastErrorOn)}</span>
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-sm text-muted-foreground">{t('common:No error recorded')}</p>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>

        <div className="border-t border-border px-4 pb-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPageSizeChange={changePageSize}
            totalCount={totalCount}
          />
        </div>
      </div>

      <Dialog
        open={isModalOpen}
        onOpenChange={(open) => {
          // Every dismissal path (X, backdrop, Esc) stays locked while the update is in flight.
          if (!saving) setIsModalOpen(open);
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('common:Update Email Status')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            {saveFailed && (
              <Alert variant="error">
                <span className="text-sm">
                  {rearmRefused
                    ? t('common:This welcome email no longer holds the starting password and cannot be sent again. Ask the company HR to use "Send welcome email" on the employee profile, which sets a new password and sends a new email.')
                    : t('common:Something went wrong. Please try again.')}
                </span>
              </Alert>
            )}
            <p className="text-sm text-muted-foreground">
              {t('common:Email to')}: <span dir="ltr">{selectedTask?.toMasked}</span>
            </p>
            <p className="text-sm text-muted-foreground">
              {t('common:Current Status')}: {selectedTask && statusBadge(selectedTask.status)}
            </p>
            <Select
              label={t('common:New Status')}
              options={statusOptions}
              value={newStatus}
              disabled={saving}
              onChange={(value: EmailTaskStatus) => setNewStatus(value)}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)} disabled={saving}>
              {t('common:Cancel')}
            </Button>
            <Button onClick={handleUpdateStatus} loading={saving} disabled={saving}>
              {t('common:Update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default SystemEmails;
